import { z } from "zod";
import { ExternalIdEntrySchema } from "./external-id.schema";

export const AppointmentSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().nullable().optional().describe("Account"),
  brand_id: z.string().uuid().nullable().optional().describe("Brand"),
  customer_role_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Customer Role"),
  team_member_role_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Team Member Role"),
  territory_id: z.string().uuid().nullable().optional().describe("Territory"),
  proposal_id: z.string().uuid().nullable().optional().describe("Proposal"),
  opportunity_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Opportunity"),
  appointment_id: z.string().nullable().optional().describe("Appointment ID"),
  appointment_date: z
    .string()
    .nullable()
    .optional()
    .describe("Appointment Date"),
  appointment_time: z
    .string()
    .nullable()
    .optional()
    .describe("Appointment Time"),
  timezone: z.string().nullable().optional().describe("Timezone"),
  appointment_type: z
    .string()
    .nullable()
    .optional()
    .describe("Appointment Type"),
  status: z
    .enum(["scheduled", "completed", "cancelled", "no_show", "rescheduled"])
    .default("scheduled")
    .describe("Status"),
  is_complete: z.boolean().default(false).describe("Complete"),
  notes: z.string().nullable().optional().describe("Notes"),
  scheduler_notes: z.string().nullable().optional().describe("Scheduler Notes"),
  custom_fields: z.record(z.unknown()).default({}).describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});

export type Appointment = z.infer<typeof AppointmentSchema>;

// ADV-1255: omit tenant-ownership and server-managed columns from the write
// surface. business_id / account_id are stamped server-side from the caller's
// TenantContext (createAppointment → tenantInsertFields; updateAppointment
// re-stamps after ...input), so accepting them inline would let a caller
// mass-assign a row into another tenant. This mirrors the sibling Insert
// schemas (Opportunity/Job/Proposal) which exclude tenant keys entirely.
export const CreateAppointmentSchema = AppointmentSchema.omit({
  id: true,
  business_id: true,
  account_id: true,
  created_at: true,
  updated_at: true,
});

export const UpdateAppointmentSchema = CreateAppointmentSchema.partial();
